import { dayOf, toCollect, type Doc, type Route, type Stop } from '@day';
import { dayWords, money, reasonWords, time } from '../app/words';
import { pinOf } from './copyRoute';

/**
 * A route sheet to print or save as a PDF (board `route-sheet`). A printed page leaves Routr, so this follows the phone's
 * export (lib/services/routes/route_export.dart) and Google's terms 3.2.3(a): nothing here is Google's. An address goes as
 * it was written, a customer's own pin as it is, a place Google found as a link to it by its place ID; never Google's
 * wording, its positions or its times (the deadline is the business's, and when a stop was handled is the courier's).
 */

/** The address as it was written; null when only Google's wording is known. A stop with no place ID never came from Google. */
export const writtenOf = (s: Pick<Stop, 'writtenAddress' | 'placeId' | 'address'>) => s.writtenAddress ?? (s.placeId ? null : s.address);

/** A link to a place Google found, by its place ID (`RouteExport.pinOf`). */
export const placeLink = (placeId: string, label: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}&query_place_id=${encodeURIComponent(placeId)}`;

export interface SheetStop {
  n: number;
  pickup: boolean;
  address: string | null;
  link: string | null;
  /** The customer's own pin, as coordinates. */
  pin: string | null;
  customer: string | null;
  note: string | null;
  packages: number;
  by: string | null;
  amount: string | null;
  /** What became of it, once handled: "Delivered 12:37 PM". */
  outcome: string | null;
}

export interface RouteSheet {
  title: string;
  courier: string;
  business: string | null;
  day: string;
  stops: SheetStop[];
  /** Any stop goes as a link because Google's wording can't leave Routr: said on the page. */
  anyByLink: boolean;
  /** A finished route's record has no names, phones or notes: said on the page. */
  noCustomers: boolean;
  /** The business's note for the whole route, while the route waits or runs (it goes when the route ends). */
  note: string | null;
  /** What a saved PDF is called. */
  fileName: string;
}

function outcomeOf(s: Stop, zone: string): string | null {
  const at = s.at != null ? ` ${time(s.at, zone)}` : '';
  if (s.state === 'done') return `${s.type === 'pickup' ? 'Picked up' : 'Delivered'}${at}`;
  if (s.state === 'failed') return `${s.type === 'pickup' ? 'Not picked up' : 'Not delivered'}${at}: ${reasonWords(s.reason)}`;
  return null;
}

/** [raw]: the route's record, for what isn't read into its stops (a customer's pin stays apart from the courier's position). */
export function routeSheet(r: Route, raw: Doc | null, o: { title: string; courier: string; business?: string | null; zone: string; now: number }): RouteSheet {
  const records = Array.isArray(raw?.stops) ? (raw!.stops as Doc[]) : [];
  const stops = r.stops.map((s): SheetStop => {
    const address = writtenOf(s);
    const rec = records.find((x) => x.id === s.id);
    const pin = !s.placeId && rec ? pinOf(rec, raw!) : null;
    const amount = toCollect(s);
    return {
      n: s.n,
      pickup: s.type === 'pickup',
      address,
      link: s.placeId ? placeLink(s.placeId, address ?? 'Stop') : null,
      pin: pin ? `${pin.lat.toFixed(6)}, ${pin.lng.toFixed(6)}` : null,
      customer: [s.name, s.phone].filter(Boolean).join(' · ') || null,
      note: [s.access, s.notes].filter((x) => x?.trim()).join(' · ') || null,
      packages: s.packages,
      by: s.deliverBy != null ? `by ${time(s.deliverBy, o.zone)}` : null,
      amount: amount > 0 ? `${money(amount, s.currency)} to collect` : null,
      outcome: outcomeOf(s, o.zone),
    };
  });
  const day = dayWords(dayOf(r.startedAt ?? r.completedAt ?? r.endedAt ?? r.sentAt ?? o.now, o.zone));
  return {
    title: o.title,
    courier: o.courier,
    business: o.business ?? null,
    day,
    stops,
    anyByLink: stops.some((s) => !s.address),
    note: typeof raw?.note === 'string' && raw.note.trim() ? raw.note.trim() : null,
    noCustomers: (r.state === 'done' || r.state === 'ended') && !stops.some((s) => s.customer || s.note),
    fileName: `${o.title} · ${o.courier} · ${day}`.replace(/[/\\:*?"<>|]/g, ' '),
  };
}
